import React, { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import axios from 'axios'
import { useSelectedWeek } from '../../../../../../contexts/SelectedWeekContext'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange } from '../../../../../../../_metronic/helpers'

interface WeekData {
  week: string
  national: number | null
}
type DoctorVisitsChartProps = {
  season: SeasonRange
  selectedWeek?: string
}
const DoctorVisitsChart: React.FC<DoctorVisitsChartProps> = ({ season }) => {

  const [visitData, setVisitData] = useState<WeekData[]>([])
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
  const [showArrow, setShowArrow] = useState<boolean>(false)
  const [missingData, setMissingData] = useState<WeekData[]>([])
  const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
  const seasonReferenceYear = 2025
  const startYear = getYearFromWeek(season.startWeek)
  const endYear = getYearFromWeek(season.endWeek)
  const startWeekNumber = getWeekFromWeek(season.startWeek) + 1

  function addMissingWeek(data: WeekData[]): WeekData[] {
    const startWeek = season.startWeek // First week in the data
    const endWeek = season.endWeek // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const missingData: WeekData[] = []
    const newData: WeekData[] = []

    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          national: null,

        })
        missingData.push({
          week: currentWeek,
          national: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= `${startYear}-W52`) {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    setMissingData(missingData)
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  const API_URL = process.env.REACT_APP_API_URL
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(API_URL + '/wastewater_viral_load_covid19s')
        setVisitData(
          addMissingWeek(response.data).filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
        )
        let endWeek = season?.endWeek?.split('-W')
        const alignedWeek =
          startYear == seasonReferenceYear
            ? selectedWeek
            : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
        const selectWeek = alignedWeek.split('-W')
        const showArrow =
          (Number(selectWeek[0]) > startYear ||
            (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
          (Number(selectWeek[0]) < Number(endWeek[0]) ||
            (Number(selectWeek[0]) === Number(endWeek[0]) &&
              Number(selectWeek[1]) <= Number(endWeek[1])))
        setShowArrow(showArrow)
        setSeasonSelectedWeek(alignedWeek);
      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error or set a default value for data
        setVisitData([])
      }
    }

    fetchData()
  }, [selectedWeek])
  // Create traces


  const trace4 = {
    x: visitData.map((entry) => entry.week),
    y: visitData.map((entry) => entry.national),
    yaxis: 'y',
    type: 'scatter',
    mode: 'lines',
    line: { color: '#203879' },
    showlegend: false,
    name: 'Viral Load in wastewater',
    hovertemplate:
      '<b>%{fullData.name}</b><br>' +
      'Week: %{x}<br>' +
      '%{fullData.name}: %{y}<extra></extra>',
  }
  const missdata = {
    x: missingData.map((entry) => entry.week),
    y: missingData.map((entry) => 0),
    type: 'scatter',
    mode: 'markers',
    yaxis: 'y',
    name: 'Missing Data',
    marker: { color: '#000000' },
    hovertemplate:
      '<b>%{fullData.name}</b><br>' +
      'Week: %{x}<br>' +
      '%{fullData.name}: %{y}<extra></extra>',
  }

  const data = [trace4, missdata]

  const layout = {
    title: 'SARS-CoV-2 viral burden in wastewater',
    barmode: 'stack',
    bargap: 0.2,
    bargroupgap: 0.1,

    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,

          color: '#203879',
        },
        showarrow: false,
      },
      {
        x: startYear == seasonReferenceYear ? selectedWeek : seasonSelectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -220,
      },
    ],

    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {},
    yaxis2: {
      titlefont: { color: '#203879' },
      tickfont: { color: '#203879' },
      overlaying: 'y',
      side: 'right',
      showgrid: false,
      zeroline: false,
    },
    legend: {
      x: 0,
      y: -0.4,
      orientation: 'h',
    },
  }
  return (
    <>
      {' '}
      {visitData.length > 0 ? (
        <div className='plotContainer'>
          <Plot data={data} layout={layout}
            config={{ responsive: true, displaylogo: false }}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      ) : (
        <p>Loading data...</p>
      )}
    </>
  )

}

export default DoctorVisitsChart
